import { ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { Check } from "lucide-react";
import type { FlatRowSelectOption } from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";
import {
	type FlatRowSelectAction,
	FlatRowSelectActionRow,
} from "web_myplann/Shared/Components/FlatRow/FlatRowSelectActionRow";

const CHECK_STROKE_WIDTH = 2.5;

type Props<TValue extends string | number = string> = {
	options: FlatRowSelectOption<TValue>[];
	value: TValue | "";
	onSelect: (value: TValue) => void;
	bottomAction?: FlatRowSelectAction;
};

export const FlatRowSelectOptionList = <
	TValue extends string | number = string,
>({
	options,
	value,
	onSelect,
	bottomAction,
}: Props<TValue>) => {
	const { palette, spacingTokens, iconSize, colors } = useTheme();

	return (
		<Stack px={spacingTokens.lg} pb={spacingTokens.xl} role="radiogroup">
			{options.map((option, index) => {
				const isSelected = String(option.value) === String(value);
				const isLast = index === options.length - 1 && !bottomAction;
				return (
					<ButtonBase
						key={String(option.value)}
						role="radio"
						aria-checked={isSelected}
						onClick={() => onSelect(option.value)}
						sx={{
							justifyContent: "space-between",
							gap: spacingTokens.md,
							py: spacingTokens.md,
							textAlign: "left",
							borderBottom: isLast
								? "none"
								: colors.border.solid(palette.grey[100]),
						}}
					>
						<Typography
							variant={isSelected ? "bodyStrong" : "body2"}
							color={isSelected ? palette.blue.main : palette.grey[900]}
							flex={1}
						>
							{option.label}
						</Typography>
						{isSelected && (
							<Check
								size={iconSize.md}
								color={palette.blue.main}
								strokeWidth={CHECK_STROKE_WIDTH}
								style={{ flexShrink: 0 }}
							/>
						)}
					</ButtonBase>
				);
			})}
			{bottomAction && <FlatRowSelectActionRow action={bottomAction} isLast />}
		</Stack>
	);
};
